// Static content for the public shell pages — About, Contact, FAQ, Terms,
// Booking Policies, Host Subscription. Copy is production-ready placeholder,
// designed to be swapped later via the api.stayeasy.io CMS endpoints.

export const ABOUT_CONTENT = {
  eyebrow: "Est. 2024 · Independent",
  headline: "Slow travel, hand-picked.",
  lede:
    "StayEasy is a small collection of privately owned homes for the traveller who prefers a quiet morning to a busy itinerary. Every stay is chosen for craft, character and the kind of light you can't fake.",
  values: [
    {
      title: "Only homes we'd stay in ourselves.",
      body:
        "Every property is visited before it goes live. If we wouldn't book it on our own weekend off, it doesn't make the collection.",
    },
    {
      title: "Fair for hosts. Honest for guests.",
      body:
        "A flat monthly subscription for hosts, a transparent nightly rate for guests. No hidden commissions, no surprise fees at checkout.",
    },
    {
      title: "Design that gets out of the way.",
      body:
        "A calm interface, an editorial voice and no dark patterns. You come to book a stay, not to fight a website.",
    },
  ],
  numbers: [
    { value: "6", label: "Countries" },
    { value: "148", label: "Homes on the collection" },
    { value: "4.91", label: "Average guest rating" },
    { value: "72h", label: "Average host reply time" },
  ],
  storyImage:
    "https://images.unsplash.com/photo-1668438712649-ffd85f756de5?crop=entropy&cs=srgb&fm=jpg&w=1800&q=85",
};

export const CONTACT_CONTENT = {
  eyebrow: "We answer everything.",
  headline: "Say hello.",
  lede:
    "Booking questions, host enquiries, press, partnerships — everything lands in the same inbox and we usually reply within a day.",
  channels: [
    { label: "General", value: "hello@stayeasy.io" },
    { label: "Hosts", value: "hosts@stayeasy.io" },
    { label: "Press", value: "press@stayeasy.io" },
  ],
  subjects: [
    "Booking a stay",
    "Hosting with StayEasy",
    "Existing booking",
    "Press & partnerships",
    "Something else",
  ],
};

export const FAQ_CATEGORIES = [
  { id: "booking", label: "Booking" },
  { id: "payments", label: "Payments" },
  { id: "accounts", label: "Accounts" },
  { id: "messaging", label: "Messaging" },
  { id: "policies", label: "Property policies" },
];

export const FAQ_ITEMS = [
  {
    category: "booking",
    q: "Do I need an account to browse?",
    a: "No — you can browse and filter the entire collection anonymously. An account is only required when you commit to a booking.",
  },
  {
    category: "booking",
    q: "How do I know the dates I've picked are available?",
    a: "Availability is checked live against the host's calendar the moment you pick dates. If a conflict appears at booking review, we flag it and let you choose again.",
  },
  {
    category: "booking",
    q: "Can I hold dates without paying?",
    a: "We hold dates for 15 minutes once you enter the booking review, giving you time to complete payment without racing against another guest.",
  },
  {
    category: "payments",
    q: "How is my payment processed?",
    a: "Payments go through Stripe Checkout. We never see or store your card details — Stripe handles the entire transaction and PCI compliance.",
  },
  {
    category: "payments",
    q: "When am I charged?",
    a: "The full booking total is charged when you complete Stripe Checkout. If the host rejects or fails to confirm, the payment is fully refunded.",
  },
  {
    category: "payments",
    q: "Do you support crypto or bank transfer?",
    a: "Not today. Stripe Checkout accepts cards, Apple Pay, Google Pay and (in supported regions) local rails like SEPA and iDEAL.",
  },
  {
    category: "accounts",
    q: "How do I reset my password?",
    a: "Head to /forgot-password, enter your email and we'll send a six-digit OTP. Use it on the reset page to choose a new password.",
  },
  {
    category: "accounts",
    q: "Why do I need to verify my email?",
    a: "Email verification protects your booking history and payment confirmations from being sent to someone else. It takes about ten seconds.",
  },
  {
    category: "messaging",
    q: "When can I message the host?",
    a: "Messaging opens the moment your booking is confirmed. You can chat with the host all the way through your stay and up to 24 hours after checkout.",
  },
  {
    category: "messaging",
    q: "Where do I see all my conversations?",
    a: "Inside your account under Messages. Every confirmed booking has its own thread, with unread counts and stay-date context.",
  },
  {
    category: "policies",
    q: "What is the standard cancellation policy?",
    a: "Free cancellation up to 7 days before check-in. Between 7 days and 48 hours: 50% refund. Within 48 hours: non-refundable. Individual properties may set stricter terms — always shown before you confirm.",
  },
  {
    category: "policies",
    q: "Are pets or events allowed?",
    a: "Pets and events are host-approved on a per-property basis. Look at the House Rules block on each property page for the exact policy.",
  },
];

export const TERMS_SECTIONS = [
  {
    id: "acceptance",
    title: "1. Acceptance of terms",
    body: [
      "By accessing or using StayEasy you agree to these Terms of Service and our Privacy Policy. If you do not agree, please do not use the platform.",
      "We may update these terms from time to time. Material changes will be posted here with a new effective date and, where required by law, communicated to you by email.",
    ],
  },
  {
    id: "eligibility",
    title: "2. Eligibility",
    body: [
      "You must be at least 18 years old and legally able to enter into binding contracts to book a stay or host on StayEasy.",
      "You are responsible for keeping your account credentials secure and for all activity that happens under your account.",
    ],
  },
  {
    id: "role",
    title: "3. StayEasy's role",
    body: [
      "StayEasy is a marketplace that connects independent hosts with guests. We are not the owner, operator or provider of any property listed on the platform.",
      "Any dispute regarding a stay is between the guest and the host. StayEasy will assist within its published policies but is not a party to the underlying rental agreement.",
    ],
  },
  {
    id: "bookings",
    title: "4. Bookings and payments",
    body: [
      "A booking is confirmed only after payment is successfully processed by Stripe and the host has not rejected the reservation within the allowed window.",
      "The price shown at booking review includes all mandatory taxes and fees; optional add-ons or damage deposits are declared separately.",
      "Refunds are governed by the cancellation policy shown on the property page at the time of booking.",
    ],
  },
  {
    id: "conduct",
    title: "5. Guest and host conduct",
    body: [
      "You agree to treat the property, the host, other guests and the neighbourhood with respect. Illegal activity, harassment and misrepresentation are grounds for immediate suspension.",
      "Hosts agree to keep listings accurate, calendars up to date and to honour confirmed bookings.",
    ],
  },
  {
    id: "content",
    title: "6. Content and reviews",
    body: [
      "By submitting a review, testimonial, photo or message you grant StayEasy a non-exclusive, worldwide licence to display and share that content on the platform and for marketing purposes.",
      "We may moderate or remove content that violates our community guidelines.",
    ],
  },
  {
    id: "liability",
    title: "7. Limitation of liability",
    body: [
      "To the maximum extent permitted by law, StayEasy is not liable for indirect, incidental or consequential damages arising from your use of the platform.",
      "Our aggregate liability is limited to the total fees you paid to StayEasy in the twelve months preceding the event giving rise to the claim.",
    ],
  },
  {
    id: "law",
    title: "8. Governing law",
    body: [
      "These terms are governed by the laws of the State of Delaware, United States, without regard to conflict-of-law principles. Any dispute will be resolved in the courts of Delaware.",
    ],
  },
];

export const POLICY_SECTIONS = [
  {
    id: "summary",
    title: "The essentials",
    body:
      "Free cancellation up to seven days before check-in. Full payment at booking. Every guest sees the exact same total before Stripe. No commission surprises for hosts.",
  },
  {
    id: "booking",
    title: "Booking & payment",
    body:
      "Bookings are confirmed after Stripe Checkout completes and the host has not rejected the request. Payment is charged in full at that point. Prices always include taxes and mandatory fees.",
  },
  {
    id: "cancellation",
    title: "Guest cancellation",
    body:
      "More than 7 days before check-in: full refund. Between 7 days and 48 hours: 50% refund. Within 48 hours: non-refundable. Individual properties may set stricter policies — this is always shown before you confirm.",
  },
  {
    id: "host",
    title: "Host cancellation",
    body:
      "If a host cancels a confirmed booking, we refund the guest in full and help them find a comparable stay. Repeat cancellations affect the host's standing on the platform.",
  },
  {
    id: "check",
    title: "Check-in & check-out",
    body:
      "Standard check-in is 3:00 PM, check-out 11:00 AM. Individual hosts may offer earlier check-in or later check-out on request — message the host through StayEasy once your booking is confirmed.",
  },
  {
    id: "damage",
    title: "Damage & security deposits",
    body:
      "Most stays do not require a deposit. Where one is required it appears on the property page and is authorised (not charged) via Stripe. Damage is handled directly between guest and host, with StayEasy support available if needed.",
  },
];

export const HOST_PLANS = [
  {
    id: "free",
    name: "Free",
    price: 0,
    tagline: "For casual hosts testing the water.",
    features: [
      "1 active listing",
      "Standard property placement",
      "Guest messaging (24h reply target)",
      "Stripe Connect payouts",
    ],
    cta: "Start free",
    featured: false,
  },
  {
    id: "starter",
    name: "Starter",
    price: 29,
    tagline: "For a single home you rent regularly.",
    features: [
      "1 active listing",
      "Priority listing placement",
      "Guest messaging + read receipts",
      "Instant payouts (24h)",
      "Custom booking policies",
    ],
    cta: "Choose Starter",
    featured: false,
  },
  {
    id: "growth",
    name: "Growth",
    price: 79,
    tagline: "For hosts building a small portfolio.",
    features: [
      "Up to 5 active listings",
      "Featured home rotation",
      "Advanced calendar tools",
      "Team access (2 seats)",
      "Discounted photography partner",
    ],
    cta: "Choose Growth",
    featured: true,
  },
  {
    id: "pro",
    name: "Pro",
    price: 199,
    tagline: "For property managers and studios.",
    features: [
      "Unlimited active listings",
      "Homepage collection placement",
      "API access + iCal sync",
      "Team access (unlimited seats)",
      "Dedicated onboarding partner",
    ],
    cta: "Talk to us",
    featured: false,
  },
];

export const HOST_FAQ = [
  {
    q: "Do you take a commission on each booking?",
    a: "No. Hosts pay a flat monthly subscription. Guests pay a small service fee to StayEasy — that's it. What you list is what you earn.",
  },
  {
    q: "What if I want to try before subscribing?",
    a: "Every plan starts with a 30-day free trial. If it doesn't feel right, cancel before day 30 and we won't charge you.",
  },
  {
    q: "How do payouts work?",
    a: "We use Stripe Connect. You link your bank once and payouts arrive within 24 hours of guest check-in (Starter and above) or on a weekly schedule (Free).",
  },
  {
    q: "Can I switch plans later?",
    a: "Yes — up or down, any time. We prorate the change so you're never billed twice.",
  },
];
